import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { X, Target, Search, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { GOAL_CATEGORIES, findCategory } from "./goalLibrary";

/**
 * AddGoalModal — 5S goal picker.
 *
 * Flow:
 *  1. PICK   — choose a category → shows ONLY exercises this athlete is already
 *              tracking in that category (deduped by title) + "+ Add new" CTA.
 *  2. ADD    — search the coach's library (strength/speed) or pick from the
 *              curated suggestions list (stamina/suppleness/skill/body comp).
 *  3. NUMBERS— set start, target, optional date → save.
 */
export const AddGoalModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState("pick"); // pick | add | numbers
  const [category, setCategory] = useState(GOAL_CATEGORIES[0]);

  // Tracked exercises (keyed by category id) — fetched once on open
  const [tracked, setTracked] = useState({});
  const [trackedLoading, setTrackedLoading] = useState(true);

  // Library search state (used in ADD step for source==='library')
  const [search, setSearch] = useState("");
  const [libResults, setLibResults] = useState([]);
  const [libLoading, setLibLoading] = useState(false);

  // Goal form state (NUMBERS step)
  const [exercise, setExercise] = useState(null);
  const [title, setTitle] = useState("");
  const [unit, setUnit] = useState("lbs");
  const [direction, setDirection] = useState("up");
  const [startValue, setStartValue] = useState("");
  const [targetValue, setTargetValue] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [saving, setSaving] = useState(false);

  // ---- Load tracked exercises once ----
  const loadTracked = useCallback(async () => {
    setTrackedLoading(true);
    try {
      const res = await fetch(`${API}/athlete/goals/tracked-exercises`, { credentials: "include" });
      if (res.ok) setTracked((await res.json()).by_category || {});
    } catch (e) { console.error("tracked fetch failed", e); }
    finally { setTrackedLoading(false); }
  }, []);

  useEffect(() => { loadTracked(); }, [loadTracked]);

  // ---- Library search (only when step=add and source=library) ----
  useEffect(() => {
    if (step !== "add" || category.source !== "library") {
      setLibResults([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLibLoading(true);
      try {
        const url = `${API}/athlete/goals/exercise-search?goal_type=${category.id}&q=${encodeURIComponent(search.trim())}&limit=80`;
        const res = await fetch(url, { credentials: "include", signal: ctrl.signal });
        if (res.ok) setLibResults((await res.json()).exercises || []);
      } catch (e) { if (e.name !== "AbortError") console.error(e); }
      finally { setLibLoading(false); }
    }, 200);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [step, category, search]);

  const trackedHere = tracked[category.id] || [];

  const goAdd = () => {
    setSearch("");
    setStep("add");
  };

  const pickExercise = (ex) => {
    setExercise(ex);
    setTitle(ex.name);
    setUnit(ex.unit || "lbs");
    setDirection(ex.direction || "up");
    setStartValue("");
    setTargetValue("");
    setStep("numbers");
  };

  const useCustom = () => {
    setExercise(null);
    setTitle("");
    const isTime = ["speed", "stamina"].includes(category.id);
    setUnit(isTime ? "sec" : (category.id === "body_comp" ? "lbs" : "reps"));
    setDirection(category.id === "body_comp" ? "down" : (isTime ? "down" : "up"));
    setStep("numbers");
  };

  const reuseTracked = (t) => {
    // Pre-populate the numbers step with the *existing* tracked exercise so the
    // athlete can quickly create a NEW progressive goal (e.g., bench 225 → 245).
    setExercise({ name: t.title, unit: t.unit, direction: t.direction });
    setTitle(t.title);
    setUnit(t.unit);
    setDirection(t.direction);
    setStartValue(String(t.current_value ?? ""));
    setTargetValue("");
    setStep("numbers");
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Give your goal a name");
    if (!startValue || !targetValue) return toast.error("Enter starting and target values");
    if (Number(startValue) === Number(targetValue)) return toast.error("Target must differ from start");

    setSaving(true);
    try {
      const res = await fetch(`${API}/athlete/goals`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          goal_type: category.id,
          unit: unit.trim() || "lbs",
          direction,
          start_value: Number(startValue),
          target_value: Number(targetValue),
          target_date: targetDate || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create goal");
      }
      toast.success("Goal set. Show up for it.");
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ============== RENDER ==============
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-goal-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-surface border border-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
      >
        <Header step={step} onBack={() => setStep(step === "numbers" ? "pick" : "pick")} onClose={onClose} />

        {step === "pick" && (
          <PickStep
            category={category}
            setCategory={setCategory}
            tracked={trackedHere}
            trackedLoading={trackedLoading}
            onAdd={goAdd}
            onPickTracked={reuseTracked}
          />
        )}

        {step === "add" && (
          <AddStep
            category={category}
            search={search}
            setSearch={setSearch}
            libResults={libResults}
            libLoading={libLoading}
            onPick={pickExercise}
            onCustom={useCustom}
            onBack={() => setStep("pick")}
          />
        )}

        {step === "numbers" && (
          <NumbersStep
            category={category}
            exercise={exercise}
            title={title} setTitle={setTitle}
            unit={unit} setUnit={setUnit}
            direction={direction} setDirection={setDirection}
            startValue={startValue} setStartValue={setStartValue}
            targetValue={targetValue} setTargetValue={setTargetValue}
            targetDate={targetDate} setTargetDate={setTargetDate}
            saving={saving}
            onBack={() => setStep("add")}
            onSubmit={submit}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

// =================== HEADER ===================
const Header = ({ step, onBack, onClose }) => {
  const titleMap = { pick: "Pick a goal", add: "Add an exercise", numbers: "Set your numbers" };
  return (
    <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
      <div className="flex items-center gap-2">
        {step !== "pick" && (
          <button onClick={onBack} className="text-text-muted hover:text-white" data-testid="goal-modal-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <Target className="w-5 h-5 text-amber-400" />
        <h2 className="font-heading text-lg font-bold text-white">{titleMap[step]}</h2>
      </div>
      <button onClick={onClose} className="text-text-muted hover:text-white" data-testid="add-goal-close">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

// =================== PICK STEP ===================
const PickStep = ({ category, setCategory, tracked, trackedLoading, onAdd, onPickTracked }) => (
  <>
    <div className="flex gap-2 overflow-x-auto scrollbar-hide px-5 pt-4 pb-2 flex-shrink-0" data-testid="goal-category-tabs">
      {GOAL_CATEGORIES.map((c) => (
        <button
          key={c.id}
          onClick={() => setCategory(c)}
          className={`flex-shrink-0 px-3 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
            category.id === c.id
              ? "bg-amber-500 text-black"
              : "bg-[#0B0F1A] border border-border text-text-secondary hover:text-white"
          }`}
          data-testid={`goal-category-${c.id}`}
        >
          <span className="text-base">{c.icon}</span>
          {c.label}
        </button>
      ))}
    </div>

    <div className="px-5 pb-2 flex-shrink-0">
      <p className="text-xs text-text-muted">{category.description}</p>
    </div>

    <div className="flex-1 overflow-y-auto px-5 pb-5">
      {trackedLoading ? (
        <div className="text-center py-8 text-xs text-text-muted">Loading…</div>
      ) : tracked.length === 0 ? (
        <EmptyTracked category={category} onAdd={onAdd} />
      ) : (
        <>
          <p className="text-[10px] uppercase tracking-widest text-text-muted font-bold mb-2 mt-1">
            Your {category.label} goals
          </p>
          <div className="space-y-1.5 mb-4" data-testid="goal-tracked-list">
            {tracked.map((t) => (
              <button
                key={t.goal_id}
                onClick={() => onPickTracked(t)}
                className="w-full text-left bg-[#0B0F1A] border border-border hover:border-amber-500/50 rounded-lg px-3 py-2.5 transition-colors flex items-center justify-between group"
                data-testid={`goal-tracked-${(t.title || "").replace(/\s+/g, "-").toLowerCase().slice(0, 40)}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white group-hover:text-amber-400 transition-colors truncate">
                    {t.title}
                  </p>
                  <p className="text-xs text-text-muted truncate">
                    {t.current_value} → {t.target_value} {t.unit}
                  </p>
                </div>
                <span className="text-xs text-amber-400 font-bold flex-shrink-0 ml-2">+ New goal</span>
              </button>
            ))}
          </div>
        </>
      )}

      <button
        onClick={onAdd}
        className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
        data-testid="goal-add-new-btn"
      >
        <Plus className="w-4 h-4" />
        Add new exercise
      </button>
    </div>
  </>
);

const EmptyTracked = ({ category, onAdd }) => (
  <div className="bg-[#0B0F1A] border border-dashed border-border rounded-xl p-6 text-center mb-4">
    <span className="text-3xl block mb-2">{category.icon}</span>
    <p className="text-sm font-bold text-white mb-1">
      No {category.label.toLowerCase()} goals yet
    </p>
    <p className="text-xs text-text-muted">
      Tap "Add new exercise" below to pick one and set your first target.
    </p>
  </div>
);

// =================== ADD STEP ===================
const AddStep = ({ category, search, setSearch, libResults, libLoading, onPick, onCustom }) => {
  const isLibrary = category.source === "library";
  const items = isLibrary
    ? (libResults.length > 0 || search.trim() || libLoading ? libResults : category.suggestions)
    : (() => {
        const q = search.trim().toLowerCase();
        const list = category.suggestions || [];
        return q ? list.filter((s) => s.name.toLowerCase().includes(q)) : list;
      })();

  return (
    <>
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 mb-3 flex items-center gap-2">
          <span className="text-lg">{category.icon}</span>
          <p className="text-sm font-bold text-white">{category.label}</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              isLibrary
                ? `Search your coach's ${category.label.toLowerCase()} library…`
                : `Search ${category.label.toLowerCase()}…`
            }
            className="w-full bg-[#0B0F1A] border border-border rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-text-muted focus:border-amber-500/70 focus:outline-none"
            data-testid="goal-search-input"
            autoFocus
          />
        </div>
        {isLibrary && (
          <p className="text-[10px] text-text-muted mt-1.5">
            {libLoading ? "Searching…" : (search.trim() || libResults.length > 0)
              ? `${libResults.length} from your coach's library`
              : "Showing common picks — search for more"}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-5">
        <div className="space-y-1.5" data-testid="goal-add-list">
          {items.length === 0 ? (
            <p className="text-center text-xs text-text-muted py-6">
              {search.trim() ? `No matches for "${search}"` : "Nothing to show"}
            </p>
          ) : (
            items.map((ex, i) => (
              <button
                key={ex.exercise_id || `${ex.name}-${i}`}
                onClick={() => onPick(ex)}
                className="w-full text-left bg-[#0B0F1A] border border-border hover:border-amber-500/50 rounded-lg px-3 py-2.5 transition-colors flex items-center justify-between group"
                data-testid={`goal-suggest-${(ex.name || "").replace(/\s+/g, "-").toLowerCase().slice(0, 40)}`}
              >
                <span className="text-sm font-semibold text-white group-hover:text-amber-400 transition-colors truncate pr-2">
                  {ex.name}
                </span>
                <span className="text-xs text-text-muted flex-shrink-0">
                  {ex.unit} · {ex.direction === "up" ? "↑" : "↓"}
                </span>
              </button>
            ))
          )}
        </div>

        <button
          onClick={onCustom}
          className="w-full mt-3 text-xs text-text-secondary hover:text-amber-400 py-2 border border-dashed border-border rounded-lg transition-colors"
          data-testid="goal-use-custom"
        >
          Don't see it? Type your own name
        </button>
      </div>
    </>
  );
};

// =================== NUMBERS STEP ===================
const NumbersStep = ({
  category, exercise, title, setTitle, unit, setUnit, direction, setDirection,
  startValue, setStartValue, targetValue, setTargetValue, targetDate, setTargetDate,
  saving, onBack, onSubmit,
}) => (
  <>
    <div className="flex-1 overflow-y-auto p-5 space-y-4">
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
        <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-0.5">
          {category.icon} {category.label}
        </p>
        <p className="font-bold text-white truncate">{exercise ? exercise.name : "Custom goal"}</p>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-text-muted font-bold mb-1.5 block">
          Goal name
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Bench Press"
          className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2.5 text-white placeholder-text-muted focus:border-amber-500/70 focus:outline-none"
          data-testid="goal-title-input"
          maxLength={80}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-bold mb-1.5 block">Current</label>
          <input
            type="number" step="0.1" value={startValue}
            onChange={(e) => setStartValue(e.target.value)}
            placeholder="185"
            className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2.5 text-white placeholder-text-muted focus:border-amber-500/70 focus:outline-none"
            data-testid="goal-start-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-bold mb-1.5 block">Target</label>
          <input
            type="number" step="0.1" value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder="225"
            className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2.5 text-white placeholder-text-muted focus:border-amber-500/70 focus:outline-none"
            data-testid="goal-target-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-bold mb-1.5 block">Unit</label>
          <input
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="lbs"
            className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2.5 text-white placeholder-text-muted focus:border-amber-500/70 focus:outline-none"
            data-testid="goal-unit-input"
            maxLength={12}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-text-muted font-bold mb-1.5 block">Direction</label>
          <div className="flex gap-2">
            <button
              onClick={() => setDirection("up")}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                direction === "up" ? "bg-amber-500 text-black" : "bg-[#0B0F1A] border border-border text-text-secondary hover:text-white"
              }`}
              data-testid="goal-direction-up"
            >↑ Higher</button>
            <button
              onClick={() => setDirection("down")}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                direction === "down" ? "bg-amber-500 text-black" : "bg-[#0B0F1A] border border-border text-text-secondary hover:text-white"
              }`}
              data-testid="goal-direction-down"
            >↓ Lower</button>
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-text-muted font-bold mb-1.5 block">
          Target date (optional)
        </label>
        <input
          type="date" value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          className="w-full bg-[#0B0F1A] border border-border rounded-lg px-3 py-2.5 text-white focus:border-amber-500/70 focus:outline-none"
          data-testid="goal-target-date-input"
        />
      </div>
    </div>

    <div className="p-5 border-t border-border flex-shrink-0 flex gap-2">
      <button
        onClick={onBack}
        disabled={saving}
        className="px-4 py-3 bg-[#0B0F1A] border border-border text-white font-bold rounded-lg text-sm hover:border-amber-500/40 transition-colors"
        data-testid="goal-back-btn"
      >Back</button>
      <button
        onClick={onSubmit}
        disabled={saving}
        className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold py-3 rounded-lg transition-colors"
        data-testid="goal-submit-btn"
      >{saving ? "Saving…" : "Create goal"}</button>
    </div>
  </>
);

export default AddGoalModal;
